package app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// Parametros do sorteio: internos, nao aparecem na tela.
private const val COOLDOWN_DAYS = 14L
private const val STRENGTH = 2.0

private const val GOLD = "#F3C45A"
private const val MUTED = "#9A93A6"
private const val RED = "#E4394E"

@Composable
fun App() {
    val scope = rememberCoroutineScope()
    val today = remember { Dates.today() }

    var code by remember { mutableStateOf("") }
    var movies by remember { mutableStateOf<List<Movie>>(emptyList()) }
    var status by remember { mutableStateOf("Abrindo a bilheteria…") }
    var busy by remember { mutableStateOf(true) }
    var enriching by remember { mutableStateOf(false) }
    var chosen by remember { mutableStateOf<Movie?>(null) }
    var newTitle by remember { mutableStateOf("") }

    suspend fun loadInto(c: String) {
        busy = true; status = "Carregando bilhete $c…"; chosen = null
        runCatching { Api.load(c) }
            .onSuccess { movies = it; status = "Bilhete $c · ${it.size} filmes" }
            .onFailure { status = "Nao deu pra carregar: ${it.message}" }
        busy = false
    }

    fun persist(next: List<Movie>) {
        movies = next
        val c = code
        scope.launch {
            runCatching { Api.save(c, next) }
                .onFailure { status = "Nao deu pra salvar: ${it.message}" }
        }
    }

    fun newSession() {
        scope.launch {
            busy = true
            runCatching { Api.newCode() }
                .onSuccess { c ->
                    code = c; LocalStore.saveCode(c)
                    chosen = null; persist(seedMovies()); status = "Bilhete novo $c"
                }
                .onFailure { status = "Erro: ${it.message}" }
            busy = false
        }
    }

    // Add a movie, enriching with TMDb poster/year when available.
    fun addMovie(rawTitle: String) {
        val t = rawTitle.trim()
        if (t.isEmpty()) return
        newTitle = ""
        scope.launch {
            val r = runCatching { Api.tmdb(t) }.getOrNull()
            val movie = Movie(Ids.next(), r?.title ?: t, null, r?.posterPath, r?.year)
            persist(movies + movie)
        }
    }

    // Fill in posters/years for every movie still missing art.
    fun fetchPosters() {
        scope.launch {
            enriching = true
            val pending = movies.filter { it.posterPath == null }
            var current = movies
            var done = 0
            for (m in pending) {
                val r = runCatching { Api.tmdb(m.title) }.getOrNull()
                if (r != null && (r.posterPath != null || r.year != null)) {
                    current = current.map {
                        if (it.id == m.id) it.copy(posterPath = r.posterPath ?: it.posterPath, year = r.year ?: it.year) else it
                    }
                    movies = current
                    done++
                }
                status = "Buscando pôsteres… ${done} de ${pending.size}"
            }
            persist(current)
            status = if (done > 0) "Pôsteres atualizados: $done" else "Nenhum pôster novo"
            enriching = false
        }
    }

    LaunchedEffect(Unit) {
        val saved = LocalStore.savedCode()
        if (saved != null) {
            code = saved; loadInto(saved)
        } else {
            runCatching { Api.newCode() }
                .onSuccess { c ->
                    code = c; LocalStore.saveCode(c); persist(seedMovies())
                    status = "Bilhete novo $c"; busy = false
                }
                .onFailure { status = "Bilheteria fora do ar: ${it.message}"; busy = false }
        }
    }

    Div({ classes("wrap") }) {
        // brand
        Div({ classes("brand") }) {
            H1({ classes("brand-title") }) {
                Text("SORTEADOR DE ")
                Span({ classes("amp") }) { Text("FILMES") }
            }
            P({ classes("brand-tag") }) { Text("A sorte escolhe a sessão de hoje") }
        }

        // marquee hero
        MarqueeHero(
            chosen = chosen,
            movieCount = movies.size,
            today = today,
            onDraw = { chosen = Picker.pick(movies, today, COOLDOWN_DAYS, STRENGTH) },
            onWatched = {
                val c = chosen ?: return@MarqueeHero
                persist(movies.map { if (it.id == c.id) it.copy(lastWatchedDay = today) else it })
                chosen = c.copy(lastWatchedDay = today)
            },
        )

        // session ticket
        SessionTicket(code, status, busy,
            onCode = { code = it.filter { ch -> ch.isDigit() }.take(8) },
            onLoad = { if (code.length == 8) scope.launch { LocalStore.saveCode(code); loadInto(code) } },
            onNew = { newSession() })

        // add
        Div({ classes("add-row") }) {
            Input(InputType.Text) {
                classes("field"); value(newTitle); placeholder("Adicionar filme ao cartaz…")
                onInput { newTitle = it.value }
            }
            Button({ classes("btn", "btn-solid"); onClick { addMovie(newTitle) } }) { Text("Adicionar") }
        }

        // lineup
        Div({ classes("lineup-head") }) {
            Span({ classes("n") }) { Text(movies.size.toString()) }
            Span({ classes("eyebrow") }) { Text("no cartaz") }
            if (movies.any { it.posterPath == null }) {
                Button({
                    classes("btn-chip"); if (enriching) disabled(); onClick { fetchPosters() }
                }) { Text(if (enriching) "buscando…" else "buscar pôsteres") }
            }
        }

        val ordered = movies.sortedByDescending { Picker.weight(it, today, STRENGTH) }
        ordered.forEach { movie ->
            key(movie.id) {
                FilmRow(movie, today,
                    onHoje = { persist(movies.map { if (it.id == movie.id) it.copy(lastWatchedDay = today) else it }) },
                    onLimpar = { persist(movies.map { if (it.id == movie.id) it.copy(lastWatchedDay = null) else it }) },
                    onDelete = {
                        persist(movies.filterNot { it.id == movie.id })
                        if (chosen?.id == movie.id) chosen = null
                    })
            }
        }

        Div({ classes("credit") }) { Text("Pôsteres e dados por TMDB · não endossado pelo TMDB") }
    }
}

@Composable
private fun MarqueeHero(
    chosen: Movie?,
    movieCount: Int,
    today: Long,
    onDraw: () -> Unit,
    onWatched: () -> Unit,
) {
    Div({ classes("bulbs") }) {}
    Div({ classes(*if (chosen != null) arrayOf("marquee", "lit") else arrayOf("marquee")) }) {
        key(chosen?.id ?: "n$movieCount") {
            Span({ classes("eyebrow") }) { Text(if (chosen != null) "Em cartaz" else "Próxima sessão") }
            if (chosen == null) {
                Div({ classes("marquee-mark", "reveal") }) { Text("?") }
            } else {
                Poster.url(chosen.posterPath, "w342")?.let { poster ->
                    Img(src = poster, alt = chosen.title, attrs = { classes("marquee-poster", "reveal") })
                }
                Div({ classes("marquee-title", "reveal") }) {
                    Text(chosen.title + (chosen.year?.let { " ($it)" } ?: ""))
                }
            }
            Div({ classes("marquee-sub") }) {
                Text(if (chosen != null) Dates.lastWatchedLabel(chosen, today) else "$movieCount filmes no páreo")
            }
        }
        Button({ classes("btn", "btn-draw"); onClick { onDraw() } }) { Text("Sortear") }
        if (chosen != null) {
            Button({ classes("btn", "btn-ghost"); onClick { onWatched() } }) { Text("Assisti este hoje") }
        }
    }
    Div({ classes("bulbs") }) {}
}

@Composable
private fun SessionTicket(
    code: String, status: String, busy: Boolean,
    onCode: (String) -> Unit, onLoad: () -> Unit, onNew: () -> Unit,
) {
    Div({ classes("card", "ticket") }) {
        Span({ classes("eyebrow") }) { Text("Bilhete da sessão · 8 dígitos") }
        Div({ classes("code-row") }) {
            Input(InputType.Text) {
                classes("code-input"); value(code); placeholder("00000000")
                onInput { onCode(it.value) }
            }
            Button({ classes("btn", "btn-solid"); if (busy || code.length != 8) disabled(); onClick { onLoad() } }) { Text("Carregar") }
            Button({ classes("btn", "btn-outline"); if (busy) disabled(); onClick { onNew() } }) { Text("Novo") }
        }
        Div({ classes("status") }) { Text(if (busy) "● $status" else status) }
    }
}

@Composable
private fun FilmRow(movie: Movie, today: Long, onHoje: () -> Unit, onLimpar: () -> Unit, onDelete: () -> Unit) {
    val thumb = Poster.url(movie.posterPath, "w92")
    Div({ classes(*if (thumb != null) arrayOf("film", "with-poster") else arrayOf("film")) }) {
        if (thumb != null) {
            Img(src = thumb, alt = movie.title, attrs = { classes("film-thumb") })
        }
        Div({ classes("film-main") }) {
            Div({ classes("film-title") }) { Text(movie.title) }
            Div({ classes(*if (movie.lastWatchedDay == null) arrayOf("film-meta", "fresh") else arrayOf("film-meta")) }) {
                Text((movie.year?.let { "$it · " } ?: "") + Dates.lastWatchedLabel(movie, today))
            }
        }
        Button({ classes("btn-mini"); style { property("color", GOLD) }; onClick { onHoje() } }) { Text("hoje") }
        if (movie.lastWatchedDay != null) {
            Button({ classes("btn-mini"); style { property("color", MUTED) }; onClick { onLimpar() } }) { Text("limpar") }
        }
        Button({ classes("btn-mini"); style { property("color", RED) }; onClick { onDelete() } }) { Text("×") }
    }
}

private fun seedMovies(): List<Movie> = listOf(
    "O Poderoso Chefao", "Clube da Luta", "Interestelar", "Cidade de Deus",
    "Parasita", "Whiplash", "Matrix", "O Senhor dos Aneis",
).map { Movie(Ids.next(), it, null) }
